import { ChangeEvent, FormEvent, useCallback, useRef, useState } from 'react';
import type { SessionTokens } from './useSession';
import { getAuthToken } from '../library/api/authToken';
import type { ScreenVariations } from '../library/context/screenview';

export interface Message {
    id: string;
    role: 'system' | 'user' | 'assistant' | 'function' | 'data' | 'tool';
    content: string;
    data?: unknown[];
}

interface Options {
    proxyUrl: string;
    tokens: SessionTokens | null;
    onSessRefresh: (newSess: string) => void;
    screenName?: ScreenVariations | null;
}

function toWsUrl(base: string): string {
    return base.replace(/^https/, 'wss').replace(/^http/, 'ws');
}

// Decodes the sid from the unsigned payload half of a brg-consumer-assistant
// HMAC sess token (format: base64url(JSON(payload)).base64url(sig)).
// No secret needed — we only read the payload, not verify the signature.
function decodeSessSid(sess: string): string | null {
    try {
        const encodedPayload = sess.slice(0, sess.lastIndexOf('.'));
        const padded = encodedPayload
            .replaceAll('-', '+')
            .replaceAll('_', '/')
            .padEnd(encodedPayload.length + ((4 - (encodedPayload.length % 4)) % 4), '=');
        const payload = JSON.parse(atob(padded)) as { sid?: string };
        return payload.sid ?? null;
    } catch {
        return null;
    }
}

export function useAssistantChat({ proxyUrl, tokens, onSessRefresh, screenName }: Options) {
    const [messages, setMessages] = useState<Message[]>([]);
    const [input, setInput] = useState('');
    const [status, setStatus] = useState<'idle' | 'in_progress'>('idle');
    const wsRef = useRef<WebSocket | null>(null);

    const handleInputChange = useCallback((e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setInput(e.target.value);
    }, []);

    const stop = useCallback(() => {
        wsRef.current?.close(1000, 'user_stop');
        setStatus('idle');
    }, []);

    const submitMessage = useCallback((e?: FormEvent) => {
        e?.preventDefault();
        const prompt = input.trim();
        if (!prompt || !tokens) return;

        const sid = decodeSessSid(tokens.sess);
        if (!sid) return;

        const asstMsgId = `asst_${Date.now()}`;
        setMessages(prev => [...prev, { id: `user_${Date.now()}`, role: 'user', content: prompt }]);
        setInput('');
        setStatus('in_progress');

        const ws = new WebSocket(`${toWsUrl(proxyUrl)}/chat?conversationId=${sid}`);
        wsRef.current = ws;
        let chatSent = false;

        ws.onopen = () => {
            ws.send(JSON.stringify({ type: 'auth', value: getAuthToken(), chatbot_sess: tokens.sess, chatbot_aid: tokens.aid }));
        };

        let committed = false;
        let accumulated = '';

        function commit(override?: string) {
            if (committed) return;
            committed = true;
            if (override !== undefined) {
                setMessages(prev => prev.map(m =>
                    m.id === asstMsgId ? { ...m, content: override } : m
                ));
            }
            setStatus('idle');
        }

        ws.onmessage = ({ data }) => {
            const msg = JSON.parse(data as string) as Record<string, unknown>;

            if (msg.type === 'sess_refresh') {
                onSessRefresh(msg.sess as string);
                if (!chatSent) {
                    chatSent = true;
                    const chatPayload: Record<string, unknown> = { prompt };
                    if (screenName === 'recipeLiterature') {
                        chatPayload.form = { source: 'recipe-literature' };
                    }
                    ws.send(JSON.stringify({ type: 'chat', value: chatPayload }));
                }
                return;
            }
            if (msg.type === 'stream_start') {
                // Only the chunk phase starts the visible response bubble
                if (msg.streamType === 'stream_chunk') {
                    setMessages(prev => [...prev, { id: asstMsgId, role: 'assistant', content: '' }]);
                }
                return;
            }
            if (msg.type === 'stream_chunk') {
                const chunk = (msg.content as string) ?? '';
                accumulated += chunk;
                setMessages(prev => prev.map(m =>
                    m.id === asstMsgId ? { ...m, content: accumulated } : m
                ));
                return;
            }
            if (msg.type === 'stream_widget') {
                setMessages(prev => [...prev, {
                    id: `widget_${Date.now()}`,
                    role: 'data',
                    content: '',
                    data: msg.data as unknown[],
                }]);
                return;
            }
            if (msg.type === 'error') {
                commit((msg.message as string) || 'An error occurred.');
                ws.close(1000);
                return;
            }
            // Only commit on the chunk stream_end — status stream_end is a no-op
            if (msg.type === 'stream_end' && msg.streamType === 'stream_chunk') {
                commit();
                ws.close(1000);
            }
        };

        ws.onclose = () => {
            if (!committed) {
                // Partial response — commit whatever was accumulated
                commit(accumulated || 'Connection lost. Please try again.');
            }
        };
        ws.onerror = () => {
            commit(accumulated || 'Connection error. Please try again.');
        };
    }, [input, tokens, onSessRefresh, proxyUrl, screenName]);

    return { messages, input, handleInputChange, submitMessage, setInput, setMessages, stop, status };
}
